import React, { useCallback, useEffect, useMemo, useState } from 'react';

// components
import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    Input,
    Text,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import { ModalEdit, TableDrawer } from '../../../components';

import { CellProps, Column } from 'react-table';
import { format } from 'date-fns';

import { useFetch, usePost, usePut } from '../../../hooks';
import { IPaginateResponse, IVaccineReq, IVaccines } from '../../../interfaces';
import { VaccineForm } from './vaccine-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { vaccineSchema, vaccineSchemaType } from './vaccine-schema';
import { useForm } from 'react-hook-form';

interface IEdit {
    openEdit: boolean,
    id?: number;
}

export const Vaccines = (): JSX.Element => {
    const [page, setPage] = useState<number>(1);
    const [edit, setEdit] = useState<IEdit | undefined>(undefined);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const { fetchData, loading, error, data: vaccines} = useFetch<IPaginateResponse<IVaccines>>(`/vaccines?page=${page}`, undefined, false);
    const { doRequest, loading: createLoading, error: createError, data: newVaccine } = usePost<IVaccines, IVaccineReq>('/vaccines');
    const { doUpdate, loading: updateLoading, error: updateError, data: updateVaccine } = usePut<IVaccines, IVaccineReq>(`/vaccines/${edit?.id}`);

    const { register, handleSubmit, formState, clearErrors } = useForm<vaccineSchemaType>({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        shouldFocusError: true,
        resolver: yupResolver(vaccineSchema),
        criteriaMode: 'firstError',
        defaultValues: {
            name: '',
        }
    });

    useEffect(() => fetchData(), [page]);

    useEffect(() => {
        if (!loading && Boolean(error)) {
            toast({
                description: error?.message || 'Ha ocurrido un error',
                status: 'error',
                duration: 9000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
    }, [loading]);

    useEffect(() => {
        if(!createLoading && newVaccine) {
            fetchData();

            toast({
                title: 'Vacuna agregada.',
                description: 'Se ha agregado una nueva vacuna.',
                status: 'success',
                duration: 9000,
                isClosable: true,
            });
        }
    }, [createLoading]);

    useEffect(() => {
        if (!createLoading && Boolean(createError)) {
            let message = 'Ha ocurrido un error';
            if(createError?.message == 'Vaccine already exists') {
                message = 'Ya existe una vacuna con este nombre';
            }
            toast({
                description: message,
                status: 'error',
                duration: 9000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
    }, [createLoading]);

    useEffect(() => {
        if(!updateLoading && updateVaccine) {
            fetchData();
            closeEdit();

            toast({
                title: 'Vacuna actualizada.',
                status: 'success',
                duration: 9000,
                isClosable: true,
            });
        }
    }, [updateLoading]);

    useEffect(() => {
        if (!updateLoading && Boolean(updateError)) {
            toast({
                description: updateError?.message || 'Ha ocurrido un error',
                status: 'error',
                duration: 9000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
    }, [updateLoading]);

    const columns = useMemo<Column<IVaccines>[]>(() => [
        {
            id: 'name',
            Header: 'Nombre',
            accessor: 'name',
            Cell: ({ value }: CellProps<IVaccines>) => {
                return (
                    <Text textTransform={'uppercase'}>{value}</Text>
                );
            },
        },
        {
            id: 'edit',
            Header: 'Editar',
            accessor: 'id',
            Cell: ({ value }: CellProps<IVaccines>) => {
                const onPress = (): void => {
                    setEdit({
                        id: value,
                        openEdit: true,
                    });
                };

                return (
                    <Button
                        bg={'primary.400'} color={'white'} _hover={{bg: 'primary.500'}}
                        variant={'outline'}
                        size={'sm'}
                        onClick={onPress}>
                         Editar
                    </Button>
                );
            },
            disableSortBy: true,
        },
        {
            id: 'createdAt',
            Header: 'Registrado',
            accessor: 'createdAt',
            Cell: ({ value }: CellProps<IVaccines>) => {
                return (
                    <Text textTransform={'uppercase'}>{format(new Date(value), 'dd/LL/yyyy')}</Text>
                );
            },
        }
    ],  []);

    const handlePagination = (page: number): void => setPage(page);

    const onSubmit = (schema: vaccineSchemaType): void => {
        doRequest({ name: schema.name.toUpperCase() });
    };

    const onUpdate = (schema: vaccineSchemaType): void => {
        if (edit?.id) {
            doUpdate({ name: schema.name.toUpperCase() });
        }
    };

    const closeEdit = (): void => {
        setEdit({id: undefined, openEdit: false});
        clearErrors();
    };

    const editForm = useCallback((): JSX.Element => {
        return (
            <FormControl isInvalid={!!formState?.errors?.name?.message} mt={2}>
                <FormLabel htmlFor='nombre'>Nombre</FormLabel>
                <Input
                    id='nombre'
                    type='text'
                    placeholder='Ej. Sextuple'
                    focusBorderColor='primary.400'
                    bg={'white'}
                    disabled={updateLoading}
                    {...register('name', { required: true })}
                />
                <FormErrorMessage>
                    {formState.errors.name && formState.errors.name.message}
                </FormErrorMessage>
            </FormControl>
        );
    }, [formState]);

    return (
        <>
            <Heading as='h1' size='lg' noOfLines={1} ml={'1'} mb={'5'} >
                 Vacunas
            </Heading>
            <TableDrawer
                isOpen={isOpen}
                drawerTitle={'Nueva Vacuna'}
                children={<VaccineForm loading={createLoading} register={register} formState={formState} />}
                loadingCreate={createLoading}
                buttonActionTitle={'Registar'}
                buttonCancelTitle={'Cancelar'}
                onSubmit={handleSubmit(onSubmit)}
                onCloseDrawer={onClose}
                columns={columns}
                data={vaccines?.items || []}
                loading={loading}
                searchTitle={'Búsqueda local'}
                hideSearch={false}
                buttonTableTitle={'Nueva'}
                onClickButtonTable={onOpen}
                hideButtonTable={false}
                currentPage={vaccines?.currentPage || 1}
                numberOfPage={vaccines?.numberOfPages || 1}
                handlePagination={handlePagination}
            />
            <ModalEdit
                isOpen={Boolean(edit?.openEdit)}
                title='Editar Nombre'
                children={editForm}
                loading={updateLoading}
                onClose={closeEdit}
                onSubmit={handleSubmit(onUpdate)}
            />
        </>
    );
};
